import { useIonModal } from '@ionic/react'
import { useEffect, useState } from 'react'

import { UnitTaskModalOption, UnitTaskOptionsModal } from '@apps-common/apps/portal/pages/units/modals'
import { IProgramTaskModifyModalProps, ProgramTaskModifyModal } from '@apps-common/apps/portal/pages/units/pages/program-tasks/modals'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { AlertModal, IAlertModalProps, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { IAppNavigationOptions } from '@apps-common/hooks/useNavigation'
import { defaultModalAnimations, modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IManagementUnitSearchParamsModel } from '@apps-common/types/management/unit'
import { ROUTE_UNIT_PROGRAM_TASKS } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  searchParams: IManagementUnitSearchParamsModel
  searchCount: number
  navigateForward: (opts: IAppNavigationOptions) => void
}

export const UnitTaskModalButton: React.FC<IProps> = ({ searchParams, searchCount, navigateForward }) => {
  const [alertProps, setAlertProps] = useState<IAlertModalProps>()
  const [modifyProps, setModifyProps] = useState<IProgramTaskModifyModalProps>()

  const [presentOptions, dismissOptions] = useIonModal(AlertModal, {
    componentProps: alertProps,
    onDismiss: (data: string, role: string) => dismissOptions(data, role)
  })

  const [presentModify, dismissModify] = useIonModal(ProgramTaskModifyModal, {
    componentProps: modifyProps,
    onDismiss: (data: string, role: string) => dismissModify(data, role)
  })

  useEffect(() => {
    if (alertProps) {
      presentOptions({
        onDidDismiss: () => {
          setAlertProps(undefined)
        },
        ...defaultModalAnimations
      })
    }
  }, [alertProps])

  useEffect(() => {
    if (modifyProps) {
      presentModify({
        onDidDismiss: () => {
          setModifyProps(undefined)
        },
        ...defaultModalAnimations
      })
    }
  }, [modifyProps])

  const validateTaskOptions = (option: string) => {
    if (
      [UnitTaskModalOption.MonitoringChange, UnitTaskModalOption.ServerChange, UnitTaskModalOption.ServerBalance].includes(
        option as UnitTaskModalOption
      )
    ) {
      if (
        searchParams?.unprocessedCancellations === true ||
        searchParams?.hardwareToBeInvoiced === true ||
        searchParams?.pinnedUnits === true ||
        searchParams?.hasNoActivation === true
      ) {
        setAlertProps({
          subtitle: 'Invalid selection',
          status: 'warning',
          title: 'Filtered devices',
          body: (
            <div>
              <div>
                Some filter options cannot be selected when filtering for a job. please ensure the following filter options are cleared and
                try again.
                <br />
                <br />
                <ul className='ml-[20px] !list-disc'>
                  <li>Unprocessed cancellations</li>
                  <li>Hardware to be invoiced</li>
                  <li>Pinned devices</li>
                  <li>Pinned devices</li>
                </ul>
              </div>
            </div>
          ),
          options: [{ text: 'Ok', status: 'success', role: ModalDismiss.Confirm }]
        })

        return
      }

      setModifyProps({
        taskType: option,
        search: searchParams,
        searchCount
      })
    } else if (option === UnitTaskModalOption.ViewDeviceTasks) {
      navigateForward({
        routeUrl: ROUTE_UNIT_PROGRAM_TASKS.buildPath(),
        route: ROUTE_UNIT_PROGRAM_TASKS
      })
    }
  }

  return (
    <ModalButton
      component={UnitTaskOptionsModal}
      disabled={false}
      classes=''
      componentProps={{}}
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
      confirmCallbackAfterDismiss={(data: string) => validateTaskOptions(data)}
    >
      <ButtonRoundSm type='div' size='lg' className='action-warning' tooltip={{ id: `devices-job-options-tooltip`, message: 'Tasks' }}>
        <commonIcons.unitProgramTaskSchedule className='text-[1.1rem]' />
      </ButtonRoundSm>
    </ModalButton>
  )
}
